// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface } from '@stencil/core'
import { AttachInternals, Component, h, Prop, Watch } from '@stencil/core'

export type Scope = 'col' | 'row'

/**
 * The `<qds-table-head-cell>` element defines a cell as the header of a group
 * of table cells. The exact nature of this group is defined by the `scope`
 * attribute.
 *
 * @see https://quartz.se.com/build/components/table
 */
@Component({
  tag: 'qds-table-head-cell',
  shadow: true,
  styleUrl: 'table-head-cell.css',
})
export class TableHeadCell implements ComponentInterface {
  /**
   * Non-negative integer value that indicates for how many columns the cell
   * extends. Its default value is `1`. Values higher than 1000 will be
   * considered as incorrect and will be set to the default value (1).
   *
   * @webnative
   */
  @Prop({ attribute: 'colspan' }) public readonly colSpan?: number

  /**
   * Non-negative integer value that indicates for how many rows the cell
   * extends. Its default value is `1`; if its value is set to `0`, it extends
   * until the end of the table section (`<qds-table-head>` or
   * `<qds-table-body>`, even if implicitly defined), that the cell belongs to.
   * Values higher than 65534 are clipped down to 65534.
   *
   * @webnative
   */
  @Prop({ attribute: 'rowspan' }) public readonly rowSpan?: number

  /**
   * Defines the cells that the header element relates to. It may have the
   * following values:
   *
   * - `row`: The header relates to all cells of the row it belongs to.
   * - `col`: The header relates to all cells of the column it belongs to.
   *
   * If the `scope` attribute is not specified, or its value is not `row` or
   * `col`, then browsers automatically select the set of cells to which the
   * header cell applies.
   *
   * @webnative
   */
  @Prop() public readonly scope?: Scope

  @AttachInternals() private readonly internals!: ElementInternals

  @Watch('colSpan')
  protected colSpanChanged(newValue: number | undefined): void {
    this.internals.ariaColSpan =
      newValue === undefined ||
      Number.isNaN(newValue) ||
      newValue < 1 ||
      newValue > 1000
        ? '1'
        : newValue.toString()
  }

  @Watch('rowSpan')
  protected rowSpanChanged(newValue: number | undefined): void {
    this.internals.ariaRowSpan =
      newValue === undefined ||
      Number.isNaN(newValue) ||
      newValue < 1 ||
      newValue > 65_534
        ? '1'
        : newValue.toString()
  }

  @Watch('scope')
  protected scopeChanged(newValue: Scope | undefined): void {
    this.internals.role = newValue === 'row' ? 'rowheader' : 'columnheader'
  }

  public componentWillLoad(): void {
    this.colSpanChanged(this.colSpan)
    this.rowSpanChanged(this.rowSpan)
    this.scopeChanged(this.scope)
  }

  public render() {
    return (
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <th
        class="qds-table-head-cell"
        colSpan={this.colSpan}
        rowSpan={this.rowSpan}
        scope={this.scope}
        // eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role
        role="none"
      >
        <slot />
      </th>
    )
  }
}
